import { z } from 'zod';
import { isoDateStringSchema } from './common.js';
import { callOutcomeSchema } from './sales-vocabulary.js';

/**
 * One customer's history: everything the shop did for them, in one column.
 *
 * It answers the question the counter actually asks about a CUSTOMER — "what have we done for
 * this person" — so it reads across the stores that hold their work: notes and calls, orders and
 * the garments in them, money in and out, quotations, measurements, appointments,
 * alterations and designs.
 *
 * IT RECORDS WHAT HAPPENED, NOT WHAT WAS PLANNED. So a
 * delivered order is a row and a promised date is not; a garment reaching `ready` is a row and
 * its trip through every workroom stage is not — a customer with three orders of four garments
 * would otherwise open on a hundred rows of "moved to stitching".
 */
export const CUSTOMER_TIMELINE_EVENTS = [
  'customer_created',
  'customer_updated',
  'customer_merged',
  'order_booked',
  'order_delivered',
  'order_cancelled',
  /** A garment reached `ready`. The one workroom move the customer is waiting to hear about. */
  'garment_ready',
  /** A garment sent backwards, with the reason — the moves that explain a late order. */
  'garment_reworked',
  'payment_received',
  'payment_refunded',
  'payment_reversed',
  'invoice_issued',
  'credit_note_issued',
  'invoice_cancelled',
  'quotation_created',
  'quotation_sent',
  'quotation_accepted',
  'quotation_rejected',
  'measurement_taken',
  'measurement_updated',
  'appointment_booked',
  'appointment_completed',
  'appointment_cancelled',
  'alteration_booked',
  'alteration_delivered',
  'alteration_cancelled',
  'design_added',
] as const;
export type CustomerTimelineEvent = (typeof CUSTOMER_TIMELINE_EVENTS)[number];

/** What a person did, as opposed to what the system recorded: a note written, a call logged. */
export const CUSTOMER_TIMELINE_ACTIVITY_KINDS = ['note', 'call'] as const;
export type CustomerTimelineActivityKind = (typeof CUSTOMER_TIMELINE_ACTIVITY_KINDS)[number];

/**
 * Where a row came from. `activity` is something a person did; `system` is something recorded
 * as a side effect of the work — the screen renders the two differently.
 */
export const CUSTOMER_TIMELINE_SOURCES = ['activity', 'system'] as const;
export type CustomerTimelineSource = (typeof CUSTOMER_TIMELINE_SOURCES)[number];

/** Whose history a row is. Always the customer's own. */
export const CUSTOMER_TIMELINE_ORIGINS = ['customer'] as const;
export type CustomerTimelineOrigin = (typeof CUSTOMER_TIMELINE_ORIGINS)[number];

/** What a row is about, so the screen can take somebody to it. */
export const CUSTOMER_TIMELINE_REF_TYPES = [
  'order',
  'quotation',
  'measurement',
  'appointment',
  'alteration',
  'design',
] as const;
export type CustomerTimelineRefType = (typeof CUSTOMER_TIMELINE_REF_TYPES)[number];

export const customerTimelineRefSchema = z.object({
  type: z.enum(CUSTOMER_TIMELINE_REF_TYPES),
  id: z.string(),
  /** What the shop calls it — an order number, a quotation number. Null when it has none. */
  label: z.string().nullable(),
  /**
   * The order a row hangs off, when it hangs off one. A payment, an invoice, a garment and an
   * alteration all live on an order's page, so that is where the row links to.
   */
  orderId: z.string().nullable(),
});
export type CustomerTimelineRef = z.infer<typeof customerTimelineRefSchema>;

export const customerTimelineEntrySchema = z.object({
  id: z.string(),
  origin: z.enum(CUSTOMER_TIMELINE_ORIGINS),
  /** What a person did reads differently from what was recorded. */
  source: z.enum(CUSTOMER_TIMELINE_SOURCES),
  at: isoDateStringSchema,
  actorName: z.string().nullable(),
  kind: z.union([z.enum(CUSTOMER_TIMELINE_EVENTS), z.enum(CUSTOMER_TIMELINE_ACTIVITY_KINDS)]),
  /** How a call went. Null on every other row. */
  outcome: callOutcomeSchema.nullable(),
  body: z.string().nullable(),
  /** Phrased by the screen: an amount and method for a payment, a stage for a garment. */
  detail: z.record(z.string(), z.unknown()).nullable(),
  ref: customerTimelineRefSchema.nullable(),
});
export type CustomerTimelineEntry = z.infer<typeof customerTimelineEntrySchema>;

export const customerTimelineSchema = z.object({
  items: z.array(customerTimelineEntrySchema),
  /**
   * True when any one store had more than a response carries — so the screen can SAY the list is
   * cut rather than implying that is all that ever happened.
   */
  truncated: z.boolean(),
});
export type CustomerTimeline = z.infer<typeof customerTimelineSchema>;
